import { attoDeskApi } from "./attoDeskApi";

// Types
export type HelpQuickStep = { number: number; title?: string | null; description: string };
export type HelpImageMarker = { number: number; x: number; y: number; text: string };
export type HelpImage = {
  path: string;
  caption?: string | null;
  sort?: number;
  markers?: HelpImageMarker[];
};

export type HelpArticleListItem = {
  id: number;
  slug: string;
  title: string;
  summary: string | null;
  module: string;
  category: string;
  route_pattern: string | null;
  has_quick: boolean;
  has_images: boolean;
  has_video: boolean;
  has_full: boolean;
  is_published: boolean;
  updated_at: string | null;
};

export type HelpArticleFull = HelpArticleListItem & {
  body_markdown: string | null;
  quick_steps: HelpQuickStep[];
  images: HelpImage[];
  video_url: string | null;
  video_storage_path: string | null;
  role_visibility: string[];
  sort_order: number;
  created_at: string | null;
  created_by: number | null;
  updated_by: number | null;
  published_at: string | null;
};

export type HelpTreeCategory = { category: string; articles: HelpArticleListItem[] };

export type HelpVersion = {
  id: number;
  version_number: number;
  change_summary: string | null;
  created_by: number | null;
  created_at: string | null;
};

type Envelope<T> = { code: number; status: boolean; message: string; data: T };

export const helpApi = attoDeskApi
  .enhanceEndpoints({ addTagTypes: ["HelpArticle", "HelpTree", "HelpVersion"] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // ── Viewer ────────────────────────────────────────────
      listHelpArticles: builder.query<
        Envelope<{ items: HelpArticleListItem[] }>,
        { module?: string; category?: string; search?: string } | void
      >({
        query: (params) => ({
          url: "/help/articles",
          method: "GET",
          params: params ?? {},
        }),
        providesTags: ["HelpArticle"],
      }),
      getHelpArticle: builder.query<Envelope<{ article: HelpArticleFull }>, string>({
        query: (slug) => ({ url: `/help/articles/${slug}`, method: "GET" }),
        providesTags: (_r, _e, slug) => [{ type: "HelpArticle", id: slug }],
      }),
      getHelpTree: builder.query<Envelope<{ tree: HelpTreeCategory[] }>, void>({
        query: () => ({ url: "/help/tree", method: "GET" }),
        providesTags: ["HelpTree"],
      }),
      getHelpContext: builder.query<
        Envelope<{
          route: string;
          article: HelpArticleFull | null;
          candidates: HelpArticleListItem[];
        }>,
        string
      >({
        query: (route) => ({ url: "/help/context", method: "GET", params: { route } }),
        providesTags: (_r, _e, route) => [{ type: "HelpArticle", id: `ctx:${route}` }],
      }),

      // ── Admin ─────────────────────────────────────────────
      adminListHelpArticles: builder.query<
        Envelope<{ items: HelpArticleListItem[] }>,
        { module?: string; category?: string; status?: "published" | "draft" | ""; search?: string } | void
      >({
        query: (params) => ({
          url: "/help/admin/articles",
          method: "GET",
          params: params ?? {},
        }),
        providesTags: ["HelpArticle"],
      }),
      adminGetHelpArticle: builder.query<Envelope<{ article: HelpArticleFull }>, number>({
        query: (id) => ({ url: `/help/admin/articles/${id}`, method: "GET" }),
        providesTags: (_r, _e, id) => [{ type: "HelpArticle", id }],
      }),
      createHelpArticle: builder.mutation<Envelope<{ article: HelpArticleFull }>, Partial<HelpArticleFull>>({
        query: (body) => ({
          url: "/help/admin/articles",
          method: "POST",
          body: JSON.stringify(body),
        }),
        invalidatesTags: ["HelpArticle", "HelpTree"],
      }),
      updateHelpArticle: builder.mutation<
        Envelope<{ article: HelpArticleFull }>,
        { id: number; body: Partial<HelpArticleFull> & { change_summary?: string } }
      >({
        query: ({ id, body }) => ({
          url: `/help/admin/articles/${id}`,
          method: "PUT",
          body: JSON.stringify(body),
        }),
        invalidatesTags: (_r, _e, arg) => [
          "HelpArticle",
          "HelpTree",
          { type: "HelpArticle", id: arg.id },
          { type: "HelpVersion", id: arg.id },
        ],
      }),
      deleteHelpArticle: builder.mutation<Envelope<{}>, number>({
        query: (id) => ({ url: `/help/admin/articles/${id}`, method: "DELETE" }),
        invalidatesTags: ["HelpArticle", "HelpTree"],
      }),
      publishHelpArticle: builder.mutation<Envelope<{ article: HelpArticleFull }>, number>({
        query: (id) => ({ url: `/help/admin/articles/${id}/publish`, method: "POST" }),
        invalidatesTags: (_r, _e, id) => ["HelpArticle", "HelpTree", { type: "HelpArticle", id }],
      }),
      unpublishHelpArticle: builder.mutation<Envelope<{ article: HelpArticleFull }>, number>({
        query: (id) => ({ url: `/help/admin/articles/${id}/unpublish`, method: "POST" }),
        invalidatesTags: (_r, _e, id) => ["HelpArticle", "HelpTree", { type: "HelpArticle", id }],
      }),
      listHelpVersions: builder.query<Envelope<{ versions: HelpVersion[] }>, number>({
        query: (id) => ({ url: `/help/admin/articles/${id}/versions`, method: "GET" }),
        providesTags: (_r, _e, id) => [{ type: "HelpVersion", id }],
      }),
      restoreHelpVersion: builder.mutation<
        Envelope<{ article: HelpArticleFull }>,
        { id: number; version: number }
      >({
        query: ({ id, version }) => ({
          url: `/help/admin/articles/${id}/restore-version/${version}`,
          method: "POST",
        }),
        invalidatesTags: (_r, _e, arg) => [
          "HelpArticle",
          "HelpTree",
          { type: "HelpArticle", id: arg.id },
          { type: "HelpVersion", id: arg.id },
        ],
      }),

      // ── Media (uploads use FormData; bypass JSON header) ─
      uploadHelpImage: builder.mutation<
        Envelope<{ path: string; url: string; mime: string; size: number }>,
        FormData
      >({
        query: (form) => ({
          url: "/help/admin/media/image",
          method: "POST",
          body: form,
        }),
      }),
      uploadHelpVideo: builder.mutation<
        Envelope<{ path: string; url: string; mime: string; size: number }>,
        FormData
      >({
        query: (form) => ({
          url: "/help/admin/media/video",
          method: "POST",
          body: form,
        }),
      }),

      // ── Import / Export ──────────────────────────────────
      exportHelpArticles: builder.query<Envelope<{ articles: any[] }>, void>({
        query: () => ({ url: "/help/admin/export", method: "GET" }),
      }),
      importHelpArticles: builder.mutation<
        Envelope<{ created: number; updated: number; skipped: number; errors: any[] }>,
        { articles: any[]; overwrite_published?: boolean }
      >({
        query: (body) => ({
          url: "/help/admin/import",
          method: "POST",
          body: JSON.stringify(body),
        }),
        invalidatesTags: ["HelpArticle", "HelpTree"],
      }),
    }),
  });

export const {
  useListHelpArticlesQuery,
  useGetHelpArticleQuery,
  useGetHelpTreeQuery,
  useGetHelpContextQuery,
  useLazyGetHelpContextQuery,
  useAdminListHelpArticlesQuery,
  useAdminGetHelpArticleQuery,
  useCreateHelpArticleMutation,
  useUpdateHelpArticleMutation,
  useDeleteHelpArticleMutation,
  usePublishHelpArticleMutation,
  useUnpublishHelpArticleMutation,
  useListHelpVersionsQuery,
  useRestoreHelpVersionMutation,
  useUploadHelpImageMutation,
  useUploadHelpVideoMutation,
  useExportHelpArticlesQuery,
  useLazyExportHelpArticlesQuery,
  useImportHelpArticlesMutation,
} = helpApi;
